/**
 * Radiators PDP (2026-10-15, PDP audit): when a colour is picked, the server re-renders the gallery with that colour's
 * picture first (snippets/product-media-gallery-content.liquid via snippets/rad-swatch-resolve.liquid) and Horizon's
 * media-gallery swaps it in at slide 1. On a phone the gallery is usually above the fold line by then, so this brings
 * it back into view once the new gallery is in place. Only for colour options, only when the gallery is fully out of
 * view, never on desktop (the carousel column is sticky there), and without smooth motion for reduced-motion users.
 */
const COLOUR = /colou?r|finish/i;
const MOBILE = window.matchMedia('(max-width: 749px)');
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

let pending = false;

document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
  if (input.closest('product-card, quick-add-dialog')) return;
  const picker = input.closest('variant-picker');
  if (!picker || !COLOUR.test(input.dataset.optionName || '')) return;
  pending = true;
}, true); // capture: runs before the picker's own change handler dispatches shopify:product:select

document.addEventListener('shopify:product:select', (event) => {
  if (!pending) return;
  pending = false;
  const target = event.target;
  if (!(target instanceof Element) || target.closest('product-card, quick-add-dialog')) return;
  const section = target.closest('.shopify-section');
  const promise = /** @type {any} */ (event).promise;
  if (!section || !promise || typeof promise.then !== 'function') return;
  promise
    .then(() => new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve))))
    .then(() => {
      if (!MOBILE.matches) return;
      const gallery = section.querySelector('media-gallery');
      if (!gallery) return;
      const rect = gallery.getBoundingClientRect();
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      const outOfView = rect.bottom <= header + 24 || rect.top >= window.innerHeight;
      if (!outOfView) return;
      const top = window.scrollY + rect.top - header - 8;
      window.scrollTo({ top: Math.max(0, top), behavior: REDUCED.matches ? 'auto' : 'smooth' });
    })
    .catch(() => {});
});

/**
 * Radiators PDP (2026-10-30, visual QC products #1 and #10): Horizon re-renders the product section on a variant change
 * but only swaps in the parts its own components own (price, picker, gallery, buy buttons). Our blocks that read the
 * selected variant (product code / MPN / GTIN line, key specifications, delivery notes) render an always-present
 * element with data-rad-variant-sync and a stable id ("RadSync-<block id>"); this copies each one's content from the
 * fetched section, so the code line follows the picker. (Some blocks have no Shopify wrapper, "tag": null, so the
 * element itself is the update target.) It also hides the "inc. VAT" note while no price is shown (an unavailable
 * combination).
 * @param {ParentNode} section the live product section
 * @param {ParentNode} html the section HTML fetched by the variant picker
 * @returns {number} how many targets changed
 */
export function syncVariantBlocks(section, html) {
  const ids = new Set();
  for (const root of [section, html]) {
    for (const el of root.querySelectorAll('[data-rad-variant-sync][id]')) {
      if (el.id) ids.add(el.id);
    }
  }
  let changed = 0;
  for (const id of ids) {
    const sel = `[id="${id.replace(/[^A-Za-z0-9_-]/g, '')}"]`;
    const current = section.querySelector(sel);
    const next = html.querySelector(sel);
    if (!current || !next) continue;
    if (current.innerHTML !== next.innerHTML) {
      // Astra r2 VQ-03: keep keyboard focus when the focused link sits in a block being replaced
      const active = typeof document !== 'undefined' ? document.activeElement : null;
      let focusHref = null;
      let focusIndex = -1;
      if (active && active !== current && typeof current.contains === 'function' && current.contains(active)) {
        const focusables = [...current.querySelectorAll('a[href], button, [tabindex]')];
        focusIndex = focusables.indexOf(active);
        focusHref = active.getAttribute ? active.getAttribute('href') : null;
      }
      current.innerHTML = next.innerHTML;
      changed += 1;
      if (focusIndex >= 0) {
        const focusables = [...current.querySelectorAll('a[href], button, [tabindex]')];
        const same = focusHref ? focusables.find((el) => el.getAttribute('href') === focusHref) : null;
        const restore = same || focusables[Math.min(focusIndex, focusables.length - 1)];
        if (restore && typeof restore.focus === 'function') restore.focus({ preventScroll: true });
      }
    }
  }
  return changed;
}

document.addEventListener('shopify:product:select', (event) => {
  const target = event.target;
  if (!(target instanceof Element) || target.closest('product-card, quick-add-dialog')) return;
  const section = target.closest('.shopify-section');
  const promise = /** @type {any} */ (event).promise;
  if (!section || !promise || typeof promise.then !== 'function') return;
  promise
    .then((result) => {
      const html = result && result.detail && result.detail.html;
      if (html && typeof html.querySelector === 'function') syncVariantBlocks(section, html);
      return new Promise((resolve) => requestAnimationFrame(resolve));
    })
    .then(() => {
      const price = section.querySelector('product-price');
      if (!price) return;
      const shown = /\d/.test(price.textContent || '');
      for (const vat of section.querySelectorAll('.rad-pmeta--vat')) vat.hidden = !shown;
    })
    .catch(() => {});
});
